import axios from 'axios'

// These would be actual API calls in a real application
// For now, we'll use mock data

interface FileItem {
  id: string
  name: string
  type: 'file' | 'folder'
  size?: number
  mimeType?: string
  modifiedAt: string
  path: string
  deletedAt?: string
}

interface ShareLink {
  id: string
  hash: string
  itemId: string
  itemName: string
  itemType: 'file' | 'folder'
  createdAt: string
  views: number
  downloads: number
}

// Generate a large number of mock files for testing
const generateMockFiles = (count: number, basePath: string = '/'): FileItem[] => {
  const result: FileItem[] = [];
  const fileTypes = [
    { ext: 'pdf', mime: 'application/pdf' },
    { ext: 'docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
    { ext: 'xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    { ext: 'jpg', mime: 'image/jpeg' },
    { ext: 'png', mime: 'image/png' },
    { ext: 'mp4', mime: 'video/mp4' },
    { ext: 'txt', mime: 'text/plain' }
  ];
  
  for (let i = 1; i <= count; i++) {
    const isFolder = Math.random() > 0.7;
    const fileType = fileTypes[Math.floor(Math.random() * fileTypes.length)];
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 30)); // Random date within last 30 days
    
    // Ensure safe path names without special characters that could cause URI encoding issues
    const safeName = isFolder ? 
      `Test Folder ${i}` : 
      `Test File ${i}.${fileType.ext}`;
    
    // Create safe paths without special characters
    const safePath = `${basePath === '/' ? '' : basePath}/${safeName}`;
    
    if (isFolder) {
      result.push({
        id: `folder_${basePath.replace(/[^a-zA-Z0-9]/g, '_')}_${i}`,
        name: safeName,
        type: 'folder',
        modifiedAt: date.toISOString(),
        path: safePath
      });
    } else {
      const size = Math.floor(Math.random() * 10 * 1024 * 1024); // Random size up to 10MB
      result.push({
        id: `file_${basePath.replace(/[^a-zA-Z0-9]/g, '_')}_${i}`,
        name: safeName,
        type: 'file',
        size: size,
        mimeType: fileType.mime,
        modifiedAt: date.toISOString(),
        path: safePath
      });
    }
  }
  
  return result;
};

// Mock data for files
const mockFiles: Record<string, FileItem[]> = {
  '/': [
    {
      id: '1',
      name: 'Documents',
      type: 'folder',
      modifiedAt: new Date().toISOString(),
      path: '/Documents'
    },
    {
      id: '2',
      name: 'Images',
      type: 'folder',
      modifiedAt: new Date().toISOString(),
      path: '/Images'
    },
    {
      id: '3',
      name: 'Videos',
      type: 'folder',
      modifiedAt: new Date().toISOString(),
      path: '/Videos'
    },
    {
      id: '4',
      name: 'Project Proposal.pdf',
      type: 'file',
      size: 1024 * 1024 * 2.5, // 2.5 MB
      mimeType: 'application/pdf',
      modifiedAt: new Date().toISOString(),
      path: '/Project Proposal.pdf'
    },
    ...generateMockFiles(20)
  ],
  '/Documents': [
    {
      id: '5',
      name: 'Report.docx',
      type: 'file',
      size: 1024 * 512, // 512 KB
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      modifiedAt: new Date().toISOString(),
      path: '/Documents/Report.docx'
    },
    {
      id: '6',
      name: 'Spreadsheet.xlsx',
      type: 'file',
      size: 1024 * 256, // 256 KB
      mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      modifiedAt: new Date().toISOString(),
      path: '/Documents/Spreadsheet.xlsx'
    },
    ...generateMockFiles(15, '/Documents')
  ],
  '/Images': [
    {
      id: '7',
      name: 'Photo1.jpg',
      type: 'file',
      size: 1024 * 1024 * 1.2, // 1.2 MB
      mimeType: 'image/jpeg',
      modifiedAt: new Date().toISOString(),
      path: '/Images/Photo1.jpg'
    },
    {
      id: '8',
      name: 'Photo2.jpg',
      type: 'file',
      size: 1024 * 1024 * 0.8, // 0.8 MB
      mimeType: 'image/jpeg',
      modifiedAt: new Date().toISOString(),
      path: '/Images/Photo2.jpg'
    },
    ...generateMockFiles(18, '/Images')
  ],
  '/Videos': [
    {
      id: '9',
      name: 'Presentation.mp4',
      type: 'file',
      size: 1024 * 1024 * 15, // 15 MB
      mimeType: 'video/mp4',
      modifiedAt: new Date().toISOString(),
      path: '/Videos/Presentation.mp4'
    },
    ...generateMockFiles(12, '/Videos')
  ]
}

// Trash bin for deleted files
let trashBin: FileItem[] = [
  {
    id: '10',
    name: 'Old Report.docx',
    type: 'file',
    size: 1024 * 450, // 450 KB
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    modifiedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
    path: '/Documents/Old Report.docx',
    deletedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString() // 5 days ago
  },
  {
    id: '11',
    name: 'Archive',
    type: 'folder',
    modifiedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(), // 15 days ago
    path: '/Archive',
    deletedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString() // 15 days ago
  },
  {
    id: '12',
    name: 'Screenshot.png',
    type: 'file',
    size: 1024 * 1024 * 0.5, // 0.5 MB
    mimeType: 'image/png',
    modifiedAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(), // 25 days ago
    path: '/Images/Screenshot.png',
    deletedAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString() // 25 days ago
  }
];

// Add more trash items for testing
for (let i = 1; i <= 25; i++) {
  const isFolder = Math.random() > 0.7;
  const daysAgo = Math.floor(Math.random() * 29) + 1; // 1-29 days ago
  const date = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
  
  if (isFolder) {
    trashBin.push({
      id: `trash_folder_${i}`,
      name: `Deleted Folder ${i}`,
      type: 'folder',
      modifiedAt: date.toISOString(),
      path: `/Deleted Folder ${i}`,
      deletedAt: date.toISOString()
    });
  } else {
    const size = Math.floor(Math.random() * 10 * 1024 * 1024); // Random size up to 10MB
    const fileTypes = ['pdf', 'docx', 'xlsx', 'jpg', 'png', 'mp4', 'txt'];
    const fileType = fileTypes[Math.floor(Math.random() * fileTypes.length)];
    
    trashBin.push({
      id: `trash_file_${i}`,
      name: `Deleted File ${i}.${fileType}`,
      type: 'file',
      size: size,
      mimeType: `application/${fileType}`,
      modifiedAt: date.toISOString(),
      path: `/Documents/Deleted File ${i}.${fileType}`,
      deletedAt: date.toISOString()
    });
  }
}

// Generate a random hash for share links
const generateHash = (): string => {
  // Use only alphanumeric characters to avoid URI encoding issues
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 7; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

// Mock data for shared links
let mockSharedLinks: ShareLink[] = [
  {
    id: 's1',
    hash: 'aB3d9F7',
    itemId: '4',
    itemName: 'Project Proposal.pdf',
    itemType: 'file',
    createdAt: new Date().toISOString(),
    views: 12,
    downloads: 5
  },
  {
    id: 's2',
    hash: 'xY7q2Z8',
    itemId: '2',
    itemName: 'Images',
    itemType: 'folder',
    createdAt: new Date().toISOString(),
    views: 8,
    downloads: 3
  }
];

// Add more shared links for testing
for (let i = 1; i <= 30; i++) {
  const isFolder = Math.random() > 0.7;
  const daysAgo = Math.floor(Math.random() * 30);
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  
  mockSharedLinks.push({
    id: `share_${i}`,
    hash: generateHash(),
    itemId: `test_${i}`,
    itemName: isFolder ? `Shared Folder ${i}` : `Shared File ${i}.pdf`,
    itemType: isFolder ? 'folder' : 'file',
    createdAt: date.toISOString(),
    views: Math.floor(Math.random() * 50),
    downloads: Math.floor(Math.random() * 20)
  });
}

// Get files in a directory
export const getFiles = async (path: string): Promise<FileItem[]> => {
  // In a real app, this would be an API call
  return new Promise((resolve) => {
    setTimeout(() => {
      // Ensure path is properly formatted to avoid URI encoding issues
      const safePath = path.replace(/\/+/g, '/');
      resolve(mockFiles[safePath] || []);
    }, 500);
  });
}

// Get files in trash
export const getTrashItems = async (): Promise<FileItem[]> => {
  // In a real app, this would be an API call
  return new Promise((resolve) => {
    setTimeout(() => {
      // Clean up trash items older than 30 days
      const now = new Date();
      const filteredTrashBin = trashBin.filter(item => {
        if (!item.deletedAt) return true;
        
        const deletedDate = new Date(item.deletedAt);
        const diffDays = Math.floor((now.getTime() - deletedDate.getTime()) / (1000 * 60 * 60 * 24));
        return diffDays < 30;
      });
      
      // Update trashBin with filtered items
      trashBin = [...filteredTrashBin];
      
      resolve(trashBin);
    }, 500);
  });
}

// Upload a file with progress callback
export const uploadFile = async (
  file: File, 
  path: string, 
  onProgress?: (progress: number) => void
): Promise<void> => {
  // In a real app, this would upload to MinIO via the backend
  return new Promise((resolve) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 5;
      if (onProgress) onProgress(progress);
      
      if (progress >= 100) {
        clearInterval(interval);
        
        // Ensure safe file name without special characters
        const fileName = file.name.replace(/[^\w\s.-]/g, '_');
        
        // Ensure safe path
        const safePath = path.replace(/\/+/g, '/');
        const fullPath = `${safePath === '/' ? '' : safePath}/${fileName}`;
        
        const newFile: FileItem = {
          id: `file_${Date.now()}`,
          name: fileName,
          type: 'file',
          size: file.size,
          mimeType: file.type,
          modifiedAt: new Date().toISOString(),
          path: fullPath
        };
        
        if (!mockFiles[safePath]) {
          mockFiles[safePath] = [];
        }
        
        mockFiles[safePath].push(newFile);
        resolve();
      }
    }, 200);
  });
}

// Create a folder
export const createFolder = async (name: string, path: string): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Ensure safe folder name without special characters
      const safeName = name.replace(/[^\w\s.-]/g, '_');
      
      // Ensure safe path
      const safePath = path.replace(/\/+/g, '/');
      const newPath = safePath === '/' ? `/${safeName}` : `${safePath}/${safeName}`;
      
      const newFolder: FileItem = {
        id: `folder_${Date.now()}`,
        name: safeName,
        type: 'folder',
        modifiedAt: new Date().toISOString(),
        path: newPath
      };
      
      if (!mockFiles[safePath]) {
        mockFiles[safePath] = [];
      }
      
      mockFiles[safePath].push(newFolder);
      mockFiles[newPath] = [];
      resolve();
    }, 500);
  });
}

// Delete an item (file or folder) - move to trash
export const deleteItem = async (itemId: string): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Find the item in all paths
      for (const path in mockFiles) {
        const itemIndex = mockFiles[path].findIndex(item => item.id === itemId);
        
        if (itemIndex !== -1) {
          const item = mockFiles[path][itemIndex];
          
          // Move to trash
          const trashItem = {
            ...item,
            deletedAt: new Date().toISOString()
          };
          
          trashBin.push(trashItem);
          
          // Remove from current location
          mockFiles[path].splice(itemIndex, 1);
          break;
        }
      }
      
      // If it's a folder, also mark its contents as deleted
      // In a real app, you'd handle this recursively for nested folders
      
      resolve();
    }, 500);
  });
}

// Permanently delete an item from trash
export const permanentlyDeleteItem = async (itemId: string): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const filteredTrashBin = trashBin.filter(item => item.id !== itemId);
      trashBin = [...filteredTrashBin];
      resolve();
    }, 500);
  });
}

// Empty trash (delete all items)
export const emptyTrash = async (): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      trashBin = [];
      resolve();
    }, 500);
  });
}

// Restore an item from trash
export const restoreItem = async (itemId: string): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const itemIndex = trashBin.findIndex(item => item.id === itemId);
      
      if (itemIndex !== -1) {
        const item = { ...trashBin[itemIndex] };
        delete item.deletedAt;
        
        // Get the path where the item should be restored
        const pathParts = item.path.split('/');
        pathParts.pop(); // Remove the filename
        const parentPath = pathParts.join('/') || '/';
        
        // Ensure the parent path exists
        if (!mockFiles[parentPath]) {
          mockFiles[parentPath] = [];
        }
        
        // Add the item back to its original location
        mockFiles[parentPath].push(item);
        
        // If it's a folder, restore its empty structure
        if (item.type === 'folder') {
          mockFiles[item.path] = [];
        }
        
        // Remove from trash
        trashBin.splice(itemIndex, 1);
      }
      
      resolve();
    }, 500);
  });
}

// Rename an item
export const renameItem = async (itemId: string, newName: string): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Ensure safe name without special characters
      const safeName = newName.replace(/[^\w\s.-]/g, '_');
      
      for (const path in mockFiles) {
        const item = mockFiles[path].find(item => item.id === itemId);
        if (item) {
          item.name = safeName;
          
          // Update path if it's a folder
          if (item.type === 'folder') {
            const oldPath = item.path;
            const pathParts = oldPath.split('/');
            pathParts.pop();
            const newPath = [...pathParts, safeName].join('/');
            item.path = newPath;
            
            // Update the path key in mockFiles
            if (mockFiles[oldPath]) {
              mockFiles[newPath] = mockFiles[oldPath];
              delete mockFiles[oldPath];
            }
          }
          
          break;
        }
      }
      
      resolve();
    }, 500);
  });
}

// Get shared links
export const getSharedLinks = async (): Promise<ShareLink[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(mockSharedLinks);
    }, 500);
  });
}

// Create a share link
export const createShareLink = async (itemId: string): Promise<ShareLink> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Find the item
      let itemName = '';
      let itemType: 'file' | 'folder' = 'file';
      
      for (const path in mockFiles) {
        const item = mockFiles[path].find(item => item.id === itemId);
        if (item) {
          itemName = item.name;
          itemType = item.type;
          break;
        }
      }
      
      if (!itemName) {
        throw new Error('Item not found');
      }
      
      // Check if a share link already exists for this item
      const existingLink = mockSharedLinks.find(link => link.itemId === itemId);
      if (existingLink) {
        resolve(existingLink);
        return;
      }
      
      // Create a new share link
      const hash = generateHash();
      const newLink: ShareLink = {
        id: `share_${Date.now()}`,
        hash,
        itemId,
        itemName,
        itemType,
        createdAt: new Date().toISOString(),
        views: 0,
        downloads: 0
      };
      
      mockSharedLinks.push(newLink);
      
      // Add the shared item to the mockSharedItems in shareService
      if (itemType === 'file') {
        // Find the file in mockFiles to get its details
        let fileDetails: FileItem | undefined;
        for (const path in mockFiles) {
          fileDetails = mockFiles[path].find(item => item.id === itemId);
          if (fileDetails) break;
        }
        
        if (fileDetails && fileDetails.mimeType) {
          // Add to mockSharedItems in shareService
          // This would be handled by the backend in a real app
          // For demo purposes, we're adding a dummy URL based on file type
          let url = '';
          if (fileDetails.mimeType.startsWith('image/')) {
            url = 'https://images.unsplash.com/photo-1682687982501-1e58ab814714';
          } else if (fileDetails.mimeType.startsWith('video/')) {
            url = 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';
          } else if (fileDetails.mimeType.includes('pdf')) {
            url = 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';
          } else {
            url = 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';
          }
          
          // This would normally update a database or storage service
          // For our mock, we're just adding it to the global object
          // @ts-ignore - This is a hack for the demo
          if (typeof window !== 'undefined') {
            // @ts-ignore
            window.mockSharedItems = window.mockSharedItems || {};
            // @ts-ignore
            window.mockSharedItems[hash] = {
              id: itemId,
              hash,
              name: fileDetails.name,
              type: 'file',
              mimeType: fileDetails.mimeType,
              size: fileDetails.size,
              url
            };
          }
        }
      } else if (itemType === 'folder') {
        // For folders, we need to find all files in the folder
        // This is simplified for the demo
        // @ts-ignore
        if (typeof window !== 'undefined') {
          // @ts-ignore
          window.mockSharedItems = window.mockSharedItems || {};
          // @ts-ignore
          window.mockSharedItems[hash] = {
            id: itemId,
            hash,
            name: itemName,
            type: 'folder',
            items: [
              {
                id: 'shared_file_1',
                hash: '',
                name: 'Shared File 1.pdf',
                type: 'file',
                mimeType: 'application/pdf',
                size: 1024 * 1024 * 1.5,
                url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'
              },
              {
                id: 'shared_file_2',
                hash: '',
                name: 'Shared Image.jpg',
                type: 'file',
                mimeType: 'image/jpeg',
                size: 1024 * 1024 * 0.8,
                url: 'https://images.unsplash.com/photo-1682687982501-1e58ab814714'
              }
            ]
          };
        }
      }
      
      resolve(newLink);
    }, 500);
  });
}

// Delete a share link
export const deleteShareLink = async (linkId: string): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const linkIndex = mockSharedLinks.findIndex(link => link.id === linkId);
      if (linkIndex !== -1) {
        const link = mockSharedLinks[linkIndex];
        // Remove from mockSharedItems in shareService
        // @ts-ignore
        if (typeof window !== 'undefined' && window.mockSharedItems) {
          // @ts-ignore
          delete window.mockSharedItems[link.hash];
        }
        
        // Remove from mockSharedLinks
        mockSharedLinks.splice(linkIndex, 1);
      }
      resolve();
    }, 500);
  });
}